/* Pampered Petal — custom components for Dawn.
   Every component is a custom element, so it re-initialises cleanly in the theme editor. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const formatMoney = (cents, format) => {
    const tpl = (format || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = Number(cents) || 0;
    const fmt = (n, decimals, thousands, decimal) => {
      const fixed = (n / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };
    return tpl.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        default: return fmt(value, 2, ',', '.');
      }
    });
  };

  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------- Carousel (hero + testimonials) ---------- */
  class PPCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pp-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.dots = Array.from(this.querySelectorAll('[data-pp-dot]'));
      this.index = 0;

      this.querySelector('[data-pp-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-pp-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.ppDot))));

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setCurrent(this.slides.indexOf(entry.target));
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));

      this.toggle = this.querySelector('[data-pp-toggle]');
      this.toggle?.addEventListener('click', () => {
        const paused = this.toggle.getAttribute('aria-pressed') === 'true';
        this.toggle.setAttribute('aria-pressed', String(!paused));
        this.toggle.setAttribute('aria-label', paused ? 'Pause slideshow' : 'Play slideshow');
        this.userPaused = !paused;
        this.userPaused ? this.stop() : this.start();
      });

      if (this.dataset.autoplay === 'true' && !reduceMotion.matches && this.slides.length > 1) {
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
        this.start();
      } else if (this.toggle) {
        this.toggle.hidden = true;
      }
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
    }

    setCurrent(i) {
      if (i < 0) return;
      this.index = i;
      this.dots.forEach((dot, d) => dot.setAttribute('aria-current', d === i ? 'true' : 'false'));
    }

    go(i) {
      if (!this.slides.length) return;
      const n = this.slides.length;
      const target = ((i % n) + n) % n;
      this.track.scrollTo({ left: this.slides[target].offsetLeft - this.track.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      this.setCurrent(target);
    }

    start() {
      if (this.userPaused || this.dataset.autoplay !== 'true' || reduceMotion.matches) return;
      this.stop();
      const speed = (Number(this.dataset.speed) || 5) * 1000;
      this.timer = window.setInterval(() => this.go(this.index + 1), speed);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }
  if (!customElements.get('pp-carousel')) customElements.define('pp-carousel', PPCarousel);

  /* ---------- Hotspots (shop the look + bundle) ---------- */
  class PPHotspots extends HTMLElement {
    connectedCallback() {
      this.spots = Array.from(this.querySelectorAll('[data-pp-spot]'));
      this.spots.forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.stopPropagation();
          const open = btn.getAttribute('aria-expanded') === 'true';
          this.closeAll();
          if (!open) this.open(btn);
        });
      });
      this.onDocClick = (event) => {
        if (!this.contains(event.target) || !event.target.closest('.pp-spot')) this.closeAll();
      };
      this.onKey = (event) => {
        if (event.key === 'Escape') this.closeAll(true);
      };
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKey);
    }

    open(btn) {
      const pop = document.getElementById(btn.getAttribute('aria-controls'));
      if (!pop) return;
      btn.setAttribute('aria-expanded', 'true');
      pop.hidden = false;
      const rect = pop.getBoundingClientRect();
      if (rect.left < 8) pop.style.transform = `translateX(calc(-50% + ${8 - rect.left}px))`;
      if (rect.right > window.innerWidth - 8) pop.style.transform = `translateX(calc(-50% - ${rect.right - window.innerWidth + 8}px))`;
      const index = this.spots.indexOf(btn);
      const items = this.querySelectorAll('.pp-look__item, .pp-bundle__item');
      items.forEach((item, i) => item.classList.toggle('is-highlighted', i === index));
    }

    closeAll(returnFocus) {
      this.spots.forEach((btn) => {
        if (btn.getAttribute('aria-expanded') === 'true' && returnFocus) btn.focus();
        btn.setAttribute('aria-expanded', 'false');
        const pop = document.getElementById(btn.getAttribute('aria-controls'));
        if (pop) {
          pop.hidden = true;
          pop.style.transform = '';
        }
      });
      this.querySelectorAll('.is-highlighted').forEach((el) => el.classList.remove('is-highlighted'));
    }
  }
  if (!customElements.get('pp-hotspots')) customElements.define('pp-hotspots', PPHotspots);

  /* ---------- Bundle: add several products at once ---------- */
  class PPBundleForm extends HTMLElement {
    connectedCallback() {
      this.format = this.dataset.currencyFormat;
      this.button = this.querySelector('[data-pp-bundle-add]');
      this.error = this.querySelector('.pp-bundle__error');
      this.querySelectorAll('[data-pp-bundle-check], select[data-pp-bundle-variant]').forEach((el) =>
        el.addEventListener('change', () => this.update())
      );
      this.button?.addEventListener('click', () => this.add());
      this.update();
    }

    rows() {
      return Array.from(this.querySelectorAll('.pp-bundle__item')).filter((row) => row.querySelector('[data-pp-bundle-variant]'));
    }

    selected() {
      return this.rows()
        .filter((row) => row.querySelector('[data-pp-bundle-check]')?.checked)
        .map((row) => {
          const field = row.querySelector('[data-pp-bundle-variant]');
          const option = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
          return { id: Number(field.value), price: Number(option?.dataset.price || 0), disabled: option?.disabled };
        })
        .filter((item) => item.id && !item.disabled);
    }

    update() {
      this.rows().forEach((row) => {
        const field = row.querySelector('select[data-pp-bundle-variant]');
        const priceEl = row.querySelector('[data-pp-bundle-price]');
        if (field && priceEl) priceEl.textContent = formatMoney(field.selectedOptions[0]?.dataset.price, this.format);
      });
      const items = this.selected();
      const total = items.reduce((sum, item) => sum + item.price, 0);
      const totalEl = this.querySelector('[data-pp-bundle-total]');
      if (totalEl) totalEl.textContent = formatMoney(total, this.format);
      if (this.button) this.button.disabled = items.length === 0;
    }

    async add() {
      const items = this.selected().map(({ id }) => ({ id, quantity: 1 }));
      if (!items.length) return;
      this.setBusy(true);
      this.error.hidden = true;
      const drawer = document.querySelector('cart-drawer');
      const body = { items };
      if (drawer) {
        body.sections = 'cart-drawer,cart-icon-bubble';
        body.sections_url = window.location.pathname;
      }
      try {
        const response = await fetch(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'These items could not be added to your cart.');
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pp-bundle', cartData: data });
        }
        if (drawer && data.sections && typeof drawer.renderContents === 'function') {
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        } else {
          window.location.href = `${rootUrl()}cart`;
        }
      } catch (err) {
        this.error.textContent = err.message;
        this.error.hidden = false;
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      if (!this.button) return;
      this.button.setAttribute('aria-busy', String(busy));
      this.button.disabled = busy;
      this.button.querySelector('.pp-bundle__add-label').hidden = busy;
      this.button.querySelector('.pp-bundle__add-busy').hidden = !busy;
      if (!busy) this.update();
    }
  }
  if (!customElements.get('pp-bundle-form')) customElements.define('pp-bundle-form', PPBundleForm);

  /* ---------- Recently viewed ---------- */
  const RV_KEY = 'pp-recently-viewed';
  const readViewed = () => {
    try {
      return JSON.parse(window.localStorage.getItem(RV_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };

  class PPRecentlyViewed extends HTMLElement {
    connectedCallback() {
      const current = this.dataset.current;
      let handles = readViewed().filter((h) => typeof h === 'string' && h);
      if (current) {
        const stored = [current, ...handles.filter((h) => h !== current)].slice(0, 12);
        try {
          window.localStorage.setItem(RV_KEY, JSON.stringify(stored));
        } catch (e) {
          /* storage unavailable */
        }
        handles = stored.filter((h) => h !== current);
      }
      const limit = Number(this.dataset.limit) || 4;
      handles = handles.slice(0, limit);
      if (!handles.length) return;
      this.render(handles);
    }

    async render(handles) {
      const root = this.dataset.root && this.dataset.root.endsWith('/') ? this.dataset.root : `${this.dataset.root || ''}/`;
      const results = await Promise.all(
        handles.map((handle) =>
          fetch(`${root}products/${encodeURIComponent(handle)}?view=pp-card`)
            .then((res) => (res.ok ? res.text() : ''))
            .catch(() => '')
        )
      );
      const list = this.querySelector('[data-pp-recent-list]');
      if (!list) return;
      const cards = results.filter((html) => html && html.includes('card-wrapper'));
      if (!cards.length) return;
      list.innerHTML = cards.map((html) => `<li class="grid__item">${html}</li>`).join('');
      this.hidden = false;
    }
  }
  if (!customElements.get('pp-recently-viewed')) customElements.define('pp-recently-viewed', PPRecentlyViewed);

  /* ---------- Sticky add to cart ---------- */
  class PPStickyATC extends HTMLElement {
    connectedCallback() {
      this.mainButton = document.querySelector('product-form [name="add"], .product-form__submit');
      if (!this.mainButton) return;
      this.hidden = false;
      this.button = this.querySelector('[data-pp-sticky-button]');
      this.priceEl = this.querySelector('[data-pp-sticky-price]');

      this.button.addEventListener('click', () => {
        if (this.mainButton.disabled) {
          this.mainButton.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
          return;
        }
        this.mainButton.click();
      });

      this.syncButton = () => {
        const label = this.mainButton.querySelector('span');
        this.button.textContent = (label ? label.textContent : this.mainButton.textContent).trim();
        this.button.disabled = this.mainButton.disabled;
      };
      this.syncButton();
      this.mutation = new MutationObserver(this.syncButton);
      this.mutation.observe(this.mainButton, { attributes: true, childList: true, subtree: true, characterData: true });

      this.footer = document.querySelector('.footer, footer');
      this.mainVisible = true;
      this.footerVisible = false;
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.mainButton) {
            this.mainVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
          } else {
            this.footerVisible = entry.isIntersecting;
          }
        });
        this.classList.toggle('is-visible', !this.mainVisible && !this.footerVisible);
      });
      this.io.observe(this.mainButton);
      if (this.footer) this.io.observe(this.footer);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event && event.data && event.data.variant;
          if (variant && this.priceEl) this.priceEl.textContent = formatMoney(variant.price, this.dataset.moneyFormat);
          window.setTimeout(this.syncButton, 50);
        });
      }
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.mutation?.disconnect();
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }
  }
  if (!customElements.get('pp-sticky-atc')) customElements.define('pp-sticky-atc', PPStickyATC);

  /* ---------- Mobile dock ---------- */
  const visible = (el) => el && el.offsetParent !== null;

  class PPMobileDock extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-pp-dock]');
        if (!btn) return;
        const action = btn.dataset.ppDock;
        if (action === 'menu') {
          const summary = document.querySelector('#Details-menu-drawer-container > summary');
          if (summary) {
            event.preventDefault();
            window.scrollTo({ top: 0, behavior: 'auto' });
            summary.click();
          }
        } else if (action === 'search') {
          const toggles = Array.from(document.querySelectorAll('details-modal.header__search > summary'));
          const toggle = toggles.find(visible) || toggles[0];
          if (toggle) {
            event.preventDefault();
            toggle.click();
          }
        } else if (action === 'cart') {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            event.preventDefault();
            drawer.open(btn);
          }
        }
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCount());
      }
    }

    disconnectedCallback() {
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }

    async refreshCount() {
      const badge = this.querySelector('[data-pp-dock-count]');
      if (!badge) return;
      try {
        const cart = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      } catch (e) {
        /* keep previous count */
      }
    }
  }
  if (!customElements.get('pp-mobile-dock')) customElements.define('pp-mobile-dock', PPMobileDock);

  /* ---------- Countdown ---------- */
  class PPCountdown extends HTMLElement {
    connectedCallback() {
      const raw = (this.dataset.end || '').trim();
      this.end = raw ? new Date(raw.replace(' ', 'T')).getTime() : NaN;
      this.done = this.parentElement.querySelector('[data-pp-done]');
      if (Number.isNaN(this.end)) {
        this.hidden = true;
        return;
      }
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.timer) window.clearInterval(this.timer);
    }

    tick() {
      const left = Math.max(0, this.end - Date.now());
      const set = (sel, value) => {
        const el = this.querySelector(sel);
        if (el) el.textContent = String(value).padStart(2, '0');
      };
      set('[data-pp-days]', Math.floor(left / 86400000));
      set('[data-pp-hours]', Math.floor((left % 86400000) / 3600000));
      set('[data-pp-minutes]', Math.floor((left % 3600000) / 60000));
      set('[data-pp-seconds]', Math.floor((left % 60000) / 1000));
      if (left === 0) {
        window.clearInterval(this.timer);
        this.hidden = true;
        if (this.done) this.done.hidden = false;
      }
    }
  }
  if (!customElements.get('pp-countdown')) customElements.define('pp-countdown', PPCountdown);
})();

/* ---------- Full-screen video ---------- */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wide = window.matchMedia('(min-width: 750px)');

  class PPVideoHero extends HTMLElement {
    connectedCallback() {
      this.base = this.querySelector('video.pp-fsv__video--base');
      this.desktop = this.querySelector('video.pp-fsv__video--desktop');
      if (!this.base && !this.desktop) return;
      this.playBtn = this.querySelector('[data-pp-video-play]');
      this.soundBtn = this.querySelector('[data-pp-video-sound]');
      this.soundLabel = this.querySelector('[data-pp-sound-label]');
      this.soundOn = false;
      this.userPaused = reduce.matches;
      this.inView = true;

      [this.base, this.desktop].filter(Boolean).forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = true;
        video.controls = false;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.setAttribute('aria-label', this.dataset.videoLabel || '');
        video.addEventListener('loadeddata', () => this.kick(video));
        video.addEventListener('canplay', () => this.kick(video));
      });

      this.onMedia = () => this.choose();
      wide.addEventListener('change', this.onMedia);
      this.choose();

      this.playBtn?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (this.userPaused) this.active.pause();
        else this.play();
        this.syncButtons();
      });

      this.soundBtn?.addEventListener('click', () => {
        this.soundOn = !this.soundOn;
        this.soundBtn.classList.remove('is-inviting');
        this.active.muted = !this.soundOn;
        if (this.soundOn && this.active.paused) {
          this.userPaused = false;
          this.play();
        }
        this.syncButtons();
      });

      this.io = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (!this.active) return;
        if (this.inView) this.play();
        else this.active.pause();
      }, { threshold: 0.1 });
      this.io.observe(this);
      this.syncButtons();
    }

    disconnectedCallback() {
      this.io?.disconnect();
      wide.removeEventListener('change', this.onMedia);
    }

    choose() {
      const next = this.desktop && wide.matches ? this.desktop : this.base || this.desktop;
      if (this.active && this.active !== next) this.active.pause();
      this.active = next;
      this.active.muted = !this.soundOn;
      if (this.active.preload !== 'auto') this.active.preload = 'auto';
      if (this.active.readyState === 0) this.active.load();
      this.play();
    }

    kick(video) {
      if (video === this.active) this.play();
    }

    play() {
      if (!this.active || this.userPaused || !this.inView) return;
      const attempt = this.active.play();
      if (attempt && attempt.catch) {
        attempt.catch(() => {
          /* Browser blocked playback with sound: fall back to muted. */
          if (!this.active.muted) {
            this.soundOn = false;
            this.active.muted = true;
            this.active.play().catch(() => {});
            this.syncButtons();
          }
        });
      }
    }

    syncButtons() {
      if (this.playBtn) {
        this.playBtn.setAttribute('aria-pressed', String(this.userPaused));
        this.playBtn.setAttribute('aria-label', this.userPaused ? 'Play video' : 'Pause video');
      }
      if (this.soundBtn) {
        this.soundBtn.setAttribute('aria-pressed', String(this.soundOn));
        this.soundBtn.setAttribute('aria-label', this.soundOn ? 'Turn sound off' : 'Turn sound on');
        if (this.soundLabel) {
          this.soundLabel.textContent = this.soundOn ? this.dataset.soundOn || 'Sound on' : this.dataset.soundOff || 'Tap for sound';
        }
      }
    }
  }
  if (!customElements.get('pp-video-hero')) customElements.define('pp-video-hero', PPVideoHero);
})();

/* Product card photo swipe: keep the dots in sync (works for cards loaded later too) */
(function () {
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (!(t instanceof Element) || !t.classList.contains('pp-swipe__track')) return;
    var i = Math.round(t.scrollLeft / Math.max(t.clientWidth, 1));
    var dots = t.parentElement.querySelectorAll('.pp-swipe__dot');
    for (var k = 0; k < dots.length; k++) dots[k].classList.toggle('is-active', k === i);
  }, { capture: true, passive: true });
})();

/* ---------- Petal Lookbook: arrows, rolling counter, progress, reveal, drifting word ---------- */
(function () {
  if (customElements.get('pp-lookbook')) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var desktop = window.matchMedia('(min-width: 990px)');

  class PPLookbook extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lb-track]');
      if (!this.track) return;
      this.cards = Array.prototype.slice.call(this.track.children);
      this.prev = this.querySelector('[data-lb-prev]');
      this.next = this.querySelector('[data-lb-next]');
      this.current = this.querySelector('[data-lb-current]');
      this.bar = this.querySelector('[data-lb-bar]');
      this.ghost = this.querySelector('.pp-lb__ghost span');
      this.index = 0;

      this.onScroll = this.update.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.update();

      // Scroll-in reveal (skipped in the theme editor and for reduced motion)
      if (!this.classList.contains('pp-lb--editor') && !reduce.matches && 'IntersectionObserver' in window) {
        this.classList.add('pp-lb--armed');
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            this.classList.add('pp-lb--in');
            this.io.disconnect();
            setTimeout(() => this.classList.add('pp-lb--done'), 2600);
          });
        }, { threshold: 0.18 });
        this.io.observe(this);
      }

      // Background word drifts slowly while the page scrolls (computers only)
      if (this.ghost && !reduce.matches) {
        this.onPage = () => {
          if (this.raf) return;
          this.raf = requestAnimationFrame(() => {
            this.raf = null;
            if (!desktop.matches) { this.style.removeProperty('--lb-drift'); return; }
            var r = this.getBoundingClientRect();
            if (r.bottom < 0 || r.top > window.innerHeight) return;
            var p = (window.innerHeight - r.top) / (window.innerHeight + r.height); // 0 → 1 through the viewport
            this.style.setProperty('--lb-drift', ((0.5 - p) * 160).toFixed(1) + 'px');
          });
        };
        window.addEventListener('scroll', this.onPage, { passive: true });
        this.onPage();
      }
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.onPage) window.removeEventListener('scroll', this.onPage);
      if (this.io) this.io.disconnect();
    }

    step() {
      var a = this.cards[0], b = this.cards[1];
      if (!a) return this.track.clientWidth;
      return b ? b.offsetLeft - a.offsetLeft : a.offsetWidth;
    }

    go(dir) {
      this.track.scrollBy({ left: dir * this.step(), behavior: reduce.matches ? 'auto' : 'smooth' });
    }

    update() {
      var t = this.track;
      var max = t.scrollWidth - t.clientWidth;
      var i = Math.min(this.cards.length - 1, Math.max(0, Math.round(t.scrollLeft / Math.max(this.step(), 1))));
      if (t.scrollLeft >= max - 4) i = this.cards.length - 1 >= 0 ? this.cards.length - 1 : 0;
      if (this.current && i !== this.index) {
        this.index = i;
        this.current.textContent = (i + 1 < 10 ? '0' : '') + (i + 1);
        this.current.classList.remove('is-rolling');
        void this.current.offsetWidth;
        this.current.classList.add('is-rolling');
      }
      if (this.bar) {
        var pct = t.scrollWidth > 0 ? ((t.scrollLeft + t.clientWidth) / t.scrollWidth) * 100 : 100;
        this.bar.style.width = Math.min(100, Math.max(8, pct)).toFixed(1) + '%';
      }
      if (this.prev) this.prev.disabled = t.scrollLeft <= 4;
      if (this.next) this.next.disabled = t.scrollLeft >= max - 4;
    }
  }
  customElements.define('pp-lookbook', PPLookbook);
})();

/* ---------- Petal Story: scroll-in reveal + gentle photo drift ---------- */
(function () {
  if (customElements.get('pp-story')) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var desktop = window.matchMedia('(min-width: 990px)');
  class PPStory extends HTMLElement {
    connectedCallback() {
      this.photo = this.querySelector('[data-st-photo]');
      if (this.classList.contains('pp-st--editor') || reduce.matches) return;
      if ('IntersectionObserver' in window) {
        this.classList.add('pp-st--armed');
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            this.classList.add('pp-st--in');
            this.io.disconnect();
          });
        }, { threshold: 0.2 });
        this.io.observe(this);
      }
      if (this.photo) {
        this.onScroll = () => {
          if (this.raf) return;
          this.raf = requestAnimationFrame(() => {
            this.raf = null;
            if (!desktop.matches) { this.photo.style.removeProperty('--st-shift'); return; }
            var r = this.getBoundingClientRect();
            if (r.bottom < 0 || r.top > window.innerHeight) return;
            var p = (window.innerHeight - r.top) / (window.innerHeight + r.height);
            this.photo.style.setProperty('--st-shift', ((0.5 - p) * 48).toFixed(1) + 'px');
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }
  customElements.define('pp-story', PPStory);
})();

/* ---------- Petal Story hotspot: tap/click to open, tap outside or Esc to close ---------- */
(function () {
  if (window.__ppSpotBound) return; window.__ppSpotBound = true;
  function closeAll(except) {
    document.querySelectorAll('[data-st-spot].is-open').forEach(function (s) {
      if (s === except) return;
      s.classList.remove('is-open');
      var b = s.querySelector('.pp-st__spot-dot'); if (b) b.setAttribute('aria-expanded', 'false');
    });
  }
  document.addEventListener('click', function (e) {
    var dot = e.target.closest && e.target.closest('.pp-st__spot-dot');
    if (dot) {
      var spot = dot.closest('[data-st-spot]');
      var open = !spot.classList.contains('is-open');
      closeAll(spot);
      spot.classList.toggle('is-open', open);
      dot.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }
    if (!(e.target.closest && e.target.closest('.pp-st__spot-card'))) closeAll(null);
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(null); });
})();

/* ---------- Petal day timeline: moments, sky, swipe, autoplay, add to bag ---------- */
(function () {
  if (customElements.get('pp-day')) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var root = function () { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; };

  class PPDay extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('.pp-day__stop'));
      this.photos = Array.prototype.slice.call(this.querySelectorAll('[data-pd-photo]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('.pp-day__panel'));
      this.items = Array.prototype.slice.call(this.querySelectorAll('.pp-day__item'));
      this.pips = Array.prototype.slice.call(this.querySelectorAll('.pp-day__pip'));
      this.count = this.tabs.length;
      if (!this.count) return;
      this.index = 0;
      this.style.setProperty('--pd-n', this.count);

      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.select(i)));
      this.items.forEach((item, i) => item.addEventListener('click', () => this.select(i)));
      var list = this.querySelector('[role="tablist"]');
      if (list) list.addEventListener('keydown', (e) => this.onKey(e));

      this.setupSwipe();
      this.setupForms();
      this.scrollMode = this.getAttribute('data-scroll') === 'true' && !reduce.matches && !this.classList.contains('pp-day--editor') && this.count > 1;
      if (this.scrollMode) this.setupScroll();
      else this.setupAutoplay();

      this.onBlockSelect = (e) => {
        var tab = this.tabs.findIndex((t) => t.contains(e.target) || t === e.target);
        if (tab > -1) { this.stop(); this.go(tab, false); this.style.setProperty('--pd-p', this.count > 1 ? tab / (this.count - 1) : 0); }
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      if (!this.scrollMode) this.go(0, false);
    }

    /* A tap on a time: in scroll mode, scroll the page to that moment; otherwise just show it */
    select(i) {
      if (this.scrollMode) this.scrollToIndex(i);
      else this.go(i, true);
    }

    /* ----- Scroll-through mode: the section stays pinned while the day plays ----- */
    setupScroll() {
      this.runway = this.querySelector('.pp-day__runway');
      this.pin = this.querySelector('.pp-day__pin');
      if (!this.runway || !this.pin) { this.scrollMode = false; this.setupAutoplay(); return; }
      this.step = Math.min(1.4, Math.max(0.4, (parseInt(this.getAttribute('data-step'), 10) || 80) / 100));
      this.header = document.querySelector('sticky-header') || document.querySelector('.section-header');
      this.dock = document.querySelector('pp-mobile-dock');
      this.classList.add('pp-day--scroll');
      this.lastW = 0; this.lastH = 0;
      this.onResize = () => {
        var w = window.innerWidth, h = window.innerHeight;
        /* ignore small height changes from mobile browser bars appearing/disappearing */
        if (w === this.lastW && Math.abs(h - this.lastH) < 120) return;
        this.lastW = w; this.lastH = h;
        this.measure();
      };
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => { this.raf = null; this.offsets(); this.update(); });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.onResize();
      this.update();
      /* fonts and images can change the layout after load */
      window.addEventListener('load', () => { this.measure(); this.update(); }, { once: true });
    }

    offsets() {
      var top = 0, bottom = 0;
      if (this.header) {
        var el = this.header, parent = el.parentElement;
        var stuck = /sticky|fixed/.test(getComputedStyle(el).position);
        if (!stuck && parent && /sticky|fixed/.test(getComputedStyle(parent).position)) { el = parent; stuck = true; }
        if (stuck) top = Math.max(0, Math.round(el.getBoundingClientRect().bottom));
      }
      if (this.dock && getComputedStyle(this.dock).display !== 'none') {
        var bar = this.dock.firstElementChild || this.dock;
        var r = bar.getBoundingClientRect();
        if (r.height && r.top < window.innerHeight) bottom = Math.max(0, Math.round(window.innerHeight - r.top));
      }
      if (top !== this.top || bottom !== this.bottom) {
        this.top = top; this.bottom = bottom;
        this.style.setProperty('--pd-top', top + 'px');
        this.style.setProperty('--pd-bottom', bottom + 'px');
        this.sizeRunway();
      }
    }

    measure() {
      this.top = -1; this.bottom = -1;
      this.offsets();
      this.sizeRunway();
    }

    sizeRunway() {
      var frame = this.pin.offsetHeight;
      this.travel = Math.round((this.count - 1) * this.step * window.innerHeight);
      this.runway.style.height = (frame + this.travel) + 'px';
    }

    progress() {
      var r = this.runway.getBoundingClientRect();
      var p = (this.top - r.top) / Math.max(1, this.travel);
      return Math.min(1, Math.max(0, p));
    }

    update() {
      var n = this.count, p = this.progress();
      var seg = Math.min(n - 1, Math.floor(p * n));
      var local = p * n - seg;
      /* the sun rests on a time, then glides to the next one as that moment arrives */
      var glide = seg < n - 1 ? Math.min(1, Math.max(0, (local - 0.72) / 0.28)) : 0;
      glide = glide * glide * (3 - 2 * glide);
      var idx = seg + (glide > 0.98 ? 1 : 0);
      if (idx !== this.index || this.index === undefined || !this.started) { this.started = true; this.go(idx, false); }
      this.style.setProperty('--pd-p', ((seg + glide) / (n - 1)).toFixed(4));
      this.classList.toggle('pp-day--scrolled', p > 0.04);
    }

    scrollToIndex(i) {
      var n = this.count;
      i = Math.min(n - 1, Math.max(0, i));
      var target = i === 0 ? 0.02 : (i + 0.35) / n;
      var r = this.runway.getBoundingClientRect();
      var y = window.scrollY + r.top - this.top + target * this.travel;
      window.scrollTo({ top: Math.round(y), behavior: 'smooth' });
    }

    disconnectedCallback() {
      this.stop();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.io) this.io.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    go(i, byUser) {
      if (byUser) this.stop();
      var n = this.count;
      i = ((i % n) + n) % n;
      this.index = i;
      this.style.setProperty('--pd-p', n > 1 ? (i / (n - 1)).toFixed(4) : 0);
      var mood = this.tabs[i].getAttribute('data-mood');
      if (mood) this.setAttribute('data-mood', mood);
      this.tabs.forEach((t, k) => {
        var on = k === i;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        t.classList.toggle('is-past', k < i);
      });
      this.photos.forEach((p, k) => { p.classList.toggle('is-active', k === i); p.setAttribute('aria-hidden', String(k !== i)); });
      this.panels.forEach((p, k) => { p.hidden = k !== i; p.classList.toggle('is-active', k === i); });
      this.items.forEach((p, k) => p.classList.toggle('is-active', k === i));
      this.pips.forEach((p, k) => p.classList.toggle('is-active', k === i));
    }

    onKey(e) {
      var map = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      var next = null;
      if (e.key in map) next = this.index + map[e.key];
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = this.count - 1;
      if (next === null) return;
      e.preventDefault();
      next = Math.min(this.count - 1, Math.max(0, next));
      this.select(next);
      if (!this.scrollMode) this.tabs[this.index].focus();
      else this.tabs[next].focus({ preventScroll: true });
    }

    setupSwipe() {
      var area = this.querySelector('[data-pd-swipe]');
      if (!area || this.count < 2) return;
      var x0 = null, y0 = null;
      area.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      area.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) this.select(this.scrollMode ? Math.min(this.count - 1, Math.max(0, this.index + (dx < 0 ? 1 : -1))) : this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    /* Autoplay: only when visible, pauses on hover/focus, ends for good once someone chooses a moment */
    setupAutoplay() {
      if (this.getAttribute('data-autoplay') !== 'true' || reduce.matches || this.classList.contains('pp-day--editor') || this.count < 2) return;
      this.speed = parseInt(this.getAttribute('data-speed'), 10) || 7000;
      this.auto = true; this.visible = false; this.hovered = false;
      var pause = () => { this.hovered = true; this.clear(); };
      var resume = () => { this.hovered = false; this.tick(); };
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) resume(); });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((e) => { this.visible = e.isIntersecting; this.visible ? this.tick() : this.clear(); });
        }, { threshold: 0.45 });
        this.io.observe(this);
      }
    }
    tick() {
      this.clear();
      if (!this.auto || !this.visible || this.hovered) return;
      this.timer = setTimeout(() => { this.go(this.index + 1, false); this.tick(); }, this.speed);
    }
    clear() { if (this.timer) { clearTimeout(this.timer); this.timer = null; } }
    stop() { this.auto = false; this.clear(); }

    setupForms() {
      this.querySelectorAll('[data-pd-form]').forEach((form) => {
        var select = form.querySelector('[data-pd-variant]');
        var panel = form.closest('.pp-day__panel');
        if (select) {
          select.addEventListener('change', () => {
            var opt = select.options[select.selectedIndex];
            var now = panel.querySelector('[data-pd-now]');
            var was = panel.querySelector('[data-pd-was]');
            if (now) now.textContent = opt.getAttribute('data-price');
            if (was) {
              var c = opt.getAttribute('data-compare');
              was.textContent = c || '';
              was.hidden = !c;
            }
          });
        }
        form.addEventListener('submit', (e) => { e.preventDefault(); this.add(form); });
      });
    }

    async add(form) {
      this.stop();
      var button = form.querySelector('[data-pd-add]');
      var error = form.querySelector('[data-pd-error]');
      var idField = form.querySelector('[name="id"]');
      if (!button || !idField) return;
      var show = (state) => {
        button.querySelector('[data-pd-label]').hidden = state !== 'idle';
        button.querySelector('[data-pd-busy]').hidden = state !== 'busy';
        button.querySelector('[data-pd-done]').hidden = state !== 'done';
        button.disabled = state === 'busy';
        button.setAttribute('aria-busy', String(state === 'busy'));
      };
      error.hidden = true;
      show('busy');

      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var body = { id: Number(idField.value), quantity: 1 };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
      }
      try {
        var response = await fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        var data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'This item could not be added to your bag.');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pp-day', productVariantId: body.id, cartData: data });
        }
        if (cart && data.sections && typeof cart.renderContents === 'function') {
          cart.classList.remove('is-empty');
          cart.renderContents(data);
          show('done');
          setTimeout(function () { show('idle'); }, 2200);
        } else {
          window.location.href = root() + 'cart';
        }
      } catch (err) {
        error.textContent = err.message;
        error.hidden = false;
        show('idle');
      }
    }
  }
  customElements.define('pp-day', PPDay);
})();

/* ---------- Ask the pup finder: chat-style product finder ---------- */
(function () {
  if (customElements.get('pp-pup-finder')) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobile = window.matchMedia('(max-width: 749px)');
  var root = function () { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; };
  var el = function (tag, cls, text) { var n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
  var plain = function (html) { var t = document.createElement('div'); t.innerHTML = html || ''; return t.textContent; };
  var hasCat = function (k) { return /\b(cat|cats|kitten|kittens|kitty|feline)\b/.test(k); };
  var hasDog = function (k) { return /\b(dog|dogs|puppy|puppies|pup|pups|canine|doggy|doggie)\b/.test(k); };

  class PPPupFinder extends HTMLElement {
    connectedCallback() {
      try { this.data = JSON.parse(this.querySelector('[data-pf-data]').textContent); } catch (e) { return; }
      this.log = this.querySelector('[data-pf-log]');
      this.answers = this.querySelector('[data-pf-answers]');
      this.foot = this.querySelector('[data-pf-foot]');
      this.steps = Array.prototype.slice.call(this.querySelectorAll('.pp-pf__steps i'));
      this.mascot = this.querySelector('.pp-pf__mascot');
      this.mascotImg = this.querySelector('[data-pf-mascot]');
      this.mascotLine = this.querySelector('[data-pf-mascot-line]');
      this.state = { pet: null, interest: null, budget: null };
      this.shown = 1; /* the first question is already on the page */
      this.querySelector('[data-pf-back]').addEventListener('click', () => this.back());
      this.querySelector('[data-pf-restart]').addEventListener('click', () => this.restart());
      this.render(false);
    }

    /* ----- state changes ----- */
    choose(key, value) {
      this.state[key] = value;
      this.render(true);
    }
    back() {
      if (this.state.budget !== null) this.state.budget = null;
      else if (this.state.interest !== null) this.state.interest = null;
      else this.state.pet = null;
      this.shown = 0;
      this.render(false, true);
    }
    restart() {
      this.state = { pet: null, interest: null, budget: null };
      this.shown = 0;
      this.render(false, true);
    }

    /* ----- the conversation, rebuilt from state ----- */
    script() {
      var d = this.data, s = this.state, m = [];
      m.push({ who: 'pup', face: 'ask', lines: [d.lines.q_pet_rendered || this.firstQuestion()] });
      if (s.pet === null) return { msgs: m, ask: 'pet', face: 'mascot', step: 1 };
      m.push({ who: 'me', text: d.pets[s.pet] });
      m.push({ who: 'pup', face: 'aww', lines: [d.lines[s.pet]] });
      if (s.interest === null) return { msgs: m, ask: 'interest', face: 'aww', step: 2 };
      var it = d.interests[s.interest];
      m.push({ who: 'me', text: it.label });
      m.push({ who: 'pup', face: 'bone', lines: [it.reply, d.lines.budget].filter(Boolean) });
      if (s.budget === null) return { msgs: m, ask: 'budget', face: 'bone', step: 3 };
      m.push({ who: 'me', text: d.budgets[s.budget].label });
      var picks = this.pick();
      m.push({ who: 'pup', face: picks.items.length ? 'happy' : 'peek', lines: [picks.items.length ? (picks.relaxed ? d.lines.relaxed : d.lines.result) : d.lines.none], picks: picks, final: true });
      return { msgs: m, ask: null, face: 'happy', step: 4 };
    }
    firstQuestion() {
      if (!this._q) {
        var b = this.querySelector('.pp-pf__msg--pup .pp-pf__bubble');
        this._q = b ? b.textContent : '';
      }
      return this._q;
    }

    render(animate, fromNav) {
      var plan = this.script();
      var msgs = plan.msgs;
      var last = msgs[msgs.length - 1];
      var needsTyping = animate && last.final && !reduce.matches;
      this.firstQuestion();
      this.log.innerHTML = '';
      msgs.forEach((msg, i) => {
        if (needsTyping && i === msgs.length - 1) return;
        this.log.appendChild(this.message(msg, i >= this.shown && animate));
      });
      this.answers.innerHTML = '';
      this.foot.hidden = this.state.pet === null;
      this.steps.forEach((s, i) => s.classList.toggle('is-on', i < Math.min(plan.step, 3)));
      this.react(plan.step === 4 ? 'happy' : plan.face, plan.step === 4);

      var finish = () => {
        if (needsTyping) {
          if (this.typing) this.typing.remove();
          this.log.appendChild(this.message(last, true));
        }
        this.shown = msgs.length;
        if (plan.ask) this.answers.appendChild(this.options(plan.ask));
        this.scrollChat(fromNav);
      };

      if (needsTyping) {
        this.react('peek', false);
        this.typing = this.message({ who: 'pup', face: 'peek', typing: true }, true);
        this.log.appendChild(this.typing);
        this.scrollChat();
        clearTimeout(this.typeTimer);
        this.typeTimer = setTimeout(() => { this.react('happy', true); finish(); }, 900);
      } else {
        finish();
      }
    }

    react(face, done) {
      var src = this.data.stickers[face] || this.data.stickers.mascot;
      if (this.mascotImg && this.mascotImg.getAttribute('src') !== src) {
        this.mascotImg.setAttribute('src', src);
        if (!reduce.matches) {
          this.mascot.classList.remove('is-react');
          void this.mascot.offsetWidth;
          this.mascot.classList.add('is-react');
        }
      }
      if (this.mascotLine) this.mascotLine.textContent = done ? this.data.lines.mascotDone || this.data.lines.mascot : this.data.lines.mascot;
    }

    scrollChat(fromNav) {
      if (!mobile.matches) {
        var lastMsg = this.log.lastElementChild;
        if (lastMsg && lastMsg.classList.contains('pp-pf__msg--picks')) this.log.scrollTop = lastMsg.offsetTop - this.log.offsetTop - 16;
        else this.log.scrollTop = this.log.scrollHeight;
        return;
      }
      if (fromNav) return;
      var target = this.answers.firstElementChild || this.log.lastElementChild;
      if (!target) return;
      var r = target.getBoundingClientRect();
      if (r.bottom > window.innerHeight - 80 || r.top < 80) target.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'center' });
    }

    /* ----- building blocks ----- */
    message(msg, animate) {
      var wrap = el('div', 'pp-pf__msg pp-pf__msg--' + msg.who);
      if (!animate) wrap.style.animation = 'none';
      if (msg.who === 'me') {
        wrap.appendChild(el('p', 'pp-pf__bubble', msg.text));
        return wrap;
      }
      var face = el('img', 'pp-pf__face');
      face.src = this.data.stickers[msg.face] || this.data.stickers.ask;
      face.alt = ''; face.width = 80; face.height = 80;
      wrap.appendChild(face);
      var col = el('div');
      col.style.cssText = 'display:flex;flex-direction:column;gap:0.6rem;min-width:0;flex:1;';
      if (msg.typing) {
        var t = el('p', 'pp-pf__bubble pp-pf__typing');
        t.setAttribute('aria-label', this.data.pup + ' is typing');
        t.innerHTML = '<i></i><i></i><i></i>';
        col.appendChild(t);
      } else {
        msg.lines.forEach((line) => {
          var b = el('p', 'pp-pf__bubble', line);
          b.style.alignSelf = 'flex-start';
          col.appendChild(b);
        });
        if (msg.picks) col.appendChild(this.picksView(msg.picks));
      }
      wrap.appendChild(col);
      if (msg.picks) wrap.classList.add('pp-pf__msg--picks');
      return wrap;
    }

    options(kind) {
      var d = this.data, box;
      if (kind === 'pet') {
        box = el('div', 'pp-pf__pills');
        ['dog', 'cat', 'both'].forEach((key) => {
          var b = el('button', 'pp-pf__pill');
          b.type = 'button';
          b.innerHTML = '<svg class="pp-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="15" r="4.5"/><circle cx="6" cy="9.5" r="2.2"/><circle cx="18" cy="9.5" r="2.2"/><circle cx="9.5" cy="5.5" r="2.1"/><circle cx="14.5" cy="5.5" r="2.1"/></svg>';
          b.appendChild(el('span', null, d.pets[key]));
          b.addEventListener('click', () => this.choose('pet', key));
          box.appendChild(b);
        });
      } else if (kind === 'interest') {
        box = el('div', 'pp-pf__cards');
        d.interests.forEach((it, i) => {
          var b = el('button', 'pp-pf__card');
          b.type = 'button';
          var pic = el('span', 'pp-pf__card-img');
          if (it.img) { var img = el('img'); img.src = it.img; img.alt = ''; img.loading = 'lazy'; img.width = 56; img.height = 56; pic.appendChild(img); }
          else pic.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="15" r="4.5"/><circle cx="6" cy="9.5" r="2.2"/><circle cx="18" cy="9.5" r="2.2"/><circle cx="9.5" cy="5.5" r="2.1"/><circle cx="14.5" cy="5.5" r="2.1"/></svg>';
          var txt = el('span', 'pp-pf__card-text');
          txt.appendChild(el('span', 'pp-pf__card-label', it.label));
          if (it.sub) txt.appendChild(el('span', 'pp-pf__card-sub', it.sub));
          b.appendChild(pic); b.appendChild(txt);
          b.addEventListener('click', () => this.choose('interest', i));
          box.appendChild(b);
        });
      } else {
        box = el('div', 'pp-pf__pills');
        d.budgets.forEach((bu, i) => {
          var b = el('button', 'pp-pf__pill', bu.label);
          b.type = 'button';
          b.addEventListener('click', () => this.choose('budget', i));
          box.appendChild(b);
        });
      }
      return box;
    }

    /* ----- choosing products ----- */
    pick() {
      var d = this.data, s = this.state, it = d.interests[s.interest], bu = d.budgets[s.budget];
      var seen = {}, all = [];
      (it.products || []).forEach(function (p) { if (!seen[p.h]) { seen[p.h] = 1; all.push(p); } });
      var pool = all;
      if (s.pet === 'dog') pool = all.filter(function (p) { return !(hasCat(p.k) && !hasDog(p.k)); });
      if (s.pet === 'cat') {
        var cats = all.filter(function (p) { return hasCat(p.k); });
        var neutral = all.filter(function (p) { return !hasCat(p.k) && !hasDog(p.k); });
        pool = cats.concat(neutral);
      }
      if (!pool.length) pool = all;
      pool = pool.filter(function (p) { return p.a; }).concat(pool.filter(function (p) { return !p.a; }));
      var inside = function (p) { return (bu.min == null || p.p >= bu.min) && (bu.max == null || p.p <= bu.max); };
      var hits = pool.filter(inside);
      if (bu.id === 'high') hits.sort(function (a, b) { return b.p - a.p; });
      var want = Math.min(d.show, pool.length);
      var relaxed = false;
      if (hits.length < Math.min(3, pool.length)) {
        relaxed = hits.length < pool.length;
        var gap = function (p) { return bu.max != null && p.p > bu.max ? p.p - bu.max : bu.min != null && p.p < bu.min ? bu.min - p.p : 0; };
        var rest = pool.filter(function (p) { return !inside(p); }).sort(function (a, b) { return gap(a) - gap(b); });
        hits = hits.concat(rest);
      }
      return { items: hits.slice(0, want), relaxed: relaxed, url: it.url, label: it.label };
    }

    picksView(picks) {
      var d = this.data;
      var box = el('div', 'pp-pf__picks');
      if (picks.items.length) {
        var row = el('div', 'pp-pf__row');
        row.setAttribute('role', 'list');
        picks.items.forEach((p) => row.appendChild(this.card(p)));
        box.appendChild(row);
      }
      var all = el('a', 'pp-pf__all', 'See all ' + picks.label.toLowerCase());
      all.href = picks.url;
      box.appendChild(all);
      return box;
    }

    card(p) {
      var d = this.data;
      var c = el('div', 'pp-pf__prod');
      c.setAttribute('role', 'listitem');
      var media = el('a', 'pp-pf__prod-img');
      media.href = p.u; media.tabIndex = -1; media.setAttribute('aria-hidden', 'true');
      if (p.i) { var img = el('img'); img.src = p.i; img.alt = ''; img.loading = 'lazy'; img.width = 240; img.height = 240; media.appendChild(img); }
      c.appendChild(media);
      var h = el('h3', 'pp-pf__prod-title'); var a = el('a', null, p.t); a.href = p.u; h.appendChild(a); c.appendChild(h);
      var price = el('p', 'pp-pf__prod-price', (p.from ? 'From ' : '') + plain(p.pm));
      if (p.cm) price.appendChild(el('s', null, plain(p.cm)));
      c.appendChild(price);
      if (p.a && p.one) {
        var btn = el('button', 'pp-pf__add', d.addLabel || 'Add to bag');
        btn.type = 'button';
        btn.addEventListener('click', () => this.add(p.v, btn, c));
        c.appendChild(btn);
      } else {
        var link = el('a', 'pp-pf__add pp-pf__add--ghost', p.a ? (d.chooseLabel || 'Choose size') : 'Sold out');
        link.href = p.u;
        c.appendChild(link);
      }
      return c;
    }

    async add(variantId, button, card) {
      var label = button.textContent;
      var old = card.querySelector('.pp-pf__err'); if (old) old.remove();
      button.disabled = true; button.textContent = 'Adding…';
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var body = { id: Number(variantId), quantity: 1 };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
      }
      try {
        var res = await fetch(root() + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
        var data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'This item could not be added to your bag.');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pp-pup-finder', productVariantId: body.id, cartData: data });
        if (cart && data.sections && typeof cart.renderContents === 'function') {
          cart.classList.remove('is-empty');
          cart.renderContents(data);
          button.textContent = 'Added to bag';
          setTimeout(function () { button.textContent = label; button.disabled = false; }, 2200);
        } else {
          window.location.href = root() + 'cart';
        }
      } catch (err) {
        card.appendChild(el('p', 'pp-pf__err', err.message));
        button.textContent = label; button.disabled = false;
      }
    }
  }
  customElements.define('pp-pup-finder', PPPupFinder);
})();

/* ---------- Promises: staggered entrance, then each icon acts out its promise ---------- */
(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  function play(li) {
    li.classList.remove('is-play');
    void li.offsetWidth;
    li.classList.add('is-play');
  }
  function setup(wrap) {
    if (wrap.dataset.ppMotion) return;
    wrap.dataset.ppMotion = '1';
    var items = Array.prototype.slice.call(wrap.querySelectorAll('.pp-promise'));
    if (!items.length || reduce.matches || !('IntersectionObserver' in window)) return;
    wrap.classList.add('is-armed');
    var io = new IntersectionObserver(function (entries) {
      var batch = entries.filter(function (e) { return e.isIntersecting; }).map(function (e) { return e.target; });
      batch.sort(function (a, b) { return items.indexOf(a) - items.indexOf(b); });
      batch.forEach(function (li, k) {
        io.unobserve(li);
        li.style.setProperty('--d', k);
        li.classList.add('is-in');
        setTimeout(function () { play(li); }, k * 140 + 450);
      });
    }, { threshold: 0.35, rootMargin: '0px 0px -8% 0px' });
    items.forEach(function (li) {
      io.observe(li);
      li.addEventListener('mouseenter', function (e) { if (li.classList.contains('is-in')) play(li); });
      li.addEventListener('click', function () { if (li.classList.contains('is-in')) play(li); });
    });
  }
  function init(root) { (root || document).querySelectorAll('.pp-promises-wrap--motion').forEach(setup); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();

/* ---------- Pampered Pack: birthday month tag, copy code, focus thank-you ---------- */
(function () {
  function init(root) {
    (root || document).querySelectorAll('.pp-pk__form').forEach(function (form) {
      if (form.dataset.pkReady) return;
      form.dataset.pkReady = '1';
      var tags = form.querySelector('[data-pk-tags]');
      var bday = form.querySelector('[data-pk-bday]');
      if (tags && bday) {
        var base = tags.value;
        form.addEventListener('submit', function () {
          tags.value = bday.value ? base + ',pet-birthday-' + bday.value : base;
        });
      }
      var copy = form.querySelector('[data-pk-copy]');
      var code = form.querySelector('[data-pk-code]');
      if (copy && code) {
        copy.addEventListener('click', function () {
          var text = code.textContent.trim();
          var done = function () { copy.textContent = 'Copied!'; setTimeout(function () { copy.textContent = 'Copy code'; }, 2000); };
          if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, done);
          else {
            var r = document.createRange(); r.selectNodeContents(code);
            var s = window.getSelection(); s.removeAllRanges(); s.addRange(r);
            try { document.execCommand('copy'); } catch (e) {}
            done();
          }
        });
      }
      var thanks = form.querySelector('[data-pk-done]');
      if (thanks) {
        thanks.scrollIntoView({ block: 'center' });
        thanks.focus({ preventScroll: true });
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
